import type { ScheduleSettings } from './releaseTypes';

/**
 * Resolve the `schedule` value to send when saving a Release QA config.
 *
 * The backend treats the field as a tri-state:
 *   - `undefined` → leave the existing schedule untouched (key omitted from the request)
 *   - `null`      → remove the schedule (delete the scheduled_jobs row)
 *   - object      → create/update the schedule
 *
 * A schedule counts as "active" only when present AND enabled — an unchecked
 * "Enable scheduled runs" box (form schedule `null`, or an `enabled: false`
 * object) means the user wants no schedule. For an existing config we compare
 * the desired state against the baseline loaded from the server so an untouched
 * schedule is omitted (no churn, and — critically — never an accidental delete).
 *
 * @param isNew - whether this is a config create (no baseline to compare against)
 * @param formSchedule - the dialog's current schedule value
 * @param baselineSchedule - the schedule as loaded from the server (null when none)
 */
export function resolveSchedulePayload(opts: {
  isNew: boolean;
  formSchedule: ScheduleSettings | null;
  baselineSchedule: ScheduleSettings | null;
}): ScheduleSettings | null | undefined {
  const normalize = (s: ScheduleSettings | null): ScheduleSettings | null =>
    s && s.enabled ? s : null;

  const desired = normalize(opts.formSchedule);
  if (opts.isNew) return desired;

  const baseline = normalize(opts.baselineSchedule);
  return JSON.stringify(desired) === JSON.stringify(baseline) ? undefined : desired;
}
